import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import ImageLightbox from "../components/ImageLightbox";
import { fetchServiceTickets, updateServiceTicketStatus, uploadServiceTicketPhoto } from "../api/api";

function formatDateTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const STATUS_LABELS = {
  received: "Received",
  review: "Review",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_CLASS = {
  received: "status-open",
  review: "status-progress",
  resolved: "status-progress",
  closed: "status-closed",
};

const MAX_PHOTOS = 5;

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function typeLabel(ticket) {
  return ticket.parent_type_name ? `${ticket.parent_type_name} — ${ticket.request_type_name}` : ticket.request_type_name;
}

function ServiceTickets() {

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [updating, setUpdating] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [pendingPhotoUrls, setPendingPhotoUrls] = useState([]);
  const [pendingFileUrls, setPendingFileUrls] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);

  const loadTickets = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchServiceTickets();
      setTickets(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const filteredTickets = statusFilter
    ? tickets.filter((t) => t.status === statusFilter)
    : tickets;

  const countByStatus = (status) => tickets.filter((t) => t.status === status).length;

  const selectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setResolutionNotes("");
    setPendingPhotoUrls([]);
    setPendingFileUrls([]);
  };

  const changeStatus = async (ticketId, newStatus) => {
    if (!resolutionNotes.trim()) {
      setError("Please add a message explaining this update before saving.");
      return;
    }
    setUpdating(true);
    setError("");
    try {
      const updated = await updateServiceTicketStatus(ticketId, newStatus, resolutionNotes.trim(), pendingPhotoUrls, pendingFileUrls);
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? updated : t)));
      setSelectedTicket(updated);
      setResolutionNotes("");
      setPendingPhotoUrls([]);
      setPendingFileUrls([]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedTicket) return;

    if (pendingPhotoUrls.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos per status change.`);
      return;
    }

    setUploadingPhoto(true);
    setError("");
    try {
      const file_url = await uploadServiceTicketPhoto(file, selectedTicket.id);
      setPendingPhotoUrls((prev) => [...prev, file_url]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedTicket) return;

    if (pendingFileUrls.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} files per status change.`);
      return;
    }

    setUploadingFile(true);
    setError("");
    try {
      const file_url = await uploadServiceTicketPhoto(file, selectedTicket.id);
      setPendingFileUrls((prev) => [...prev, file_url]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploadingFile(false);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div className="service-header">

          <h1 style={{ marginTop: 0, marginBottom: 0 }}>
            Service Requests
          </h1>

          <div className="service-count">
            {tickets.length}
          </div>

        </div>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        <div className="filter-row">

          <button className={statusFilter === "" ? "active-filter" : ""} onClick={() => setStatusFilter("")}>
            All ({tickets.length})
          </button>

          <button className={statusFilter === "received" ? "active-filter" : ""} onClick={() => setStatusFilter("received")}>
            Received ({countByStatus("received")})
          </button>

          <button className={statusFilter === "review" ? "active-filter" : ""} onClick={() => setStatusFilter("review")}>
            Review ({countByStatus("review")})
          </button>

          <button className={statusFilter === "resolved" ? "active-filter" : ""} onClick={() => setStatusFilter("resolved")}>
            Resolved ({countByStatus("resolved")})
          </button>

          <button className={statusFilter === "closed" ? "active-filter" : ""} onClick={() => setStatusFilter("closed")}>
            Closed ({countByStatus("closed")})
          </button>

        </div>

        {loading && <p>Loading tickets…</p>}

        {!loading && filteredTickets.length === 0 && <p>No tickets match this filter.</p>}

        {!loading && filteredTickets.length > 0 && (
          <div className="table-card">

            <table className="service-table">

              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Plot / Owner</th>
                  <th>Raised</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {
                  filteredTickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>{ticket.ticket_number}</td>
                      <td>{typeLabel(ticket)}</td>
                      <td>{ticket.plot_number ? `${ticket.plot_number} • ${ticket.owner_name || "—"}` : "—"}</td>
                      <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                      <td>
                        <span className={STATUS_CLASS[ticket.status]}>
                          {STATUS_LABELS[ticket.status]}
                        </span>
                      </td>
                      <td>
                        <button className="view-btn" onClick={() => selectTicket(ticket)}>
                          {ticket.status === "received" ? "Respond" : "View"}
                        </button>
                      </td>
                    </tr>
                  ))
                }
              </tbody>

            </table>

          </div>
        )}

        {
          selectedTicket && (

            <div className="request-details">

              <div className="details-top">
                <h3>
                  {selectedTicket.ticket_number} — {typeLabel(selectedTicket)}
                </h3>
                <span className={STATUS_CLASS[selectedTicket.status]}>
                  {STATUS_LABELS[selectedTicket.status]}
                </span>
              </div>

              <p className="details-owner">
                Plot {selectedTicket.plot_number || "—"} • {selectedTicket.owner_name || "—"} • Raised {new Date(selectedTicket.created_at).toLocaleDateString()}
              </p>

              {selectedTicket.description && (
                <p className="details-message">
                  "{selectedTicket.description}"
                </p>
              )}

              {selectedTicket.status === "closed" ? (
                <p style={{ color: "#666", marginTop: "12px" }}>
                  This ticket has been closed by the customer — see the history below.
                </p>
              ) : (
                <>
                  <label style={{ display: "block", fontWeight: 600, marginTop: "12px", marginBottom: "6px" }}>
                    Message for the customer <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    rows="3"
                    style={{ width: "100%" }}
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Explain what changed…"
                  />

                  <div style={{ marginTop: "12px" }}>
                    <label style={{ fontWeight: 600 }}>
                      Photos ({pendingPhotoUrls.length}/{MAX_PHOTOS}) — optional
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                      {pendingPhotoUrls.map((url) => (
                        <img
                          key={url} src={url} alt="Newly uploaded"
                          onClick={() => setViewerUrl(url)}
                          style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "6px", border: "2px solid #2e7d32", cursor: "pointer" }}
                        />
                      ))}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto || pendingPhotoUrls.length >= MAX_PHOTOS}
                      style={{ marginTop: "8px" }}
                    />
                  </div>

                  <div style={{ marginTop: "12px" }}>
                    <label style={{ fontWeight: 600 }}>
                      Files ({pendingFileUrls.length}/{MAX_PHOTOS}) — optional
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                      {pendingFileUrls.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "13px" }}>📎 {url.split("/").pop()}</a>
                      ))}
                    </div>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploadingFile || pendingFileUrls.length >= MAX_PHOTOS}
                      style={{ marginTop: "8px" }}
                    />
                  </div>

                  <div className="response-actions" style={{ marginTop: "16px" }}>

                    <button
                      className="progress-btn"
                      disabled={updating || selectedTicket.status === "review"}
                      onClick={() => changeStatus(selectedTicket.id, "review")}
                    >
                      {updating ? "Updating…" : "Mark In Review"}
                    </button>

                    <button
                      className="progress-btn"
                      disabled={updating || selectedTicket.status === "resolved"}
                      onClick={() => changeStatus(selectedTicket.id, "resolved")}
                    >
                      {updating ? "Updating…" : "Mark Resolved"}
                    </button>

                  </div>
                </>
              )}

              {selectedTicket.status_history?.length > 0 && (
                <div style={{ marginTop: "20px", borderTop: "1px solid #eee", paddingTop: "12px" }}>
                  <h4 style={{ marginBottom: "10px" }}>History</h4>
                  {selectedTicket.status_history.map((h) => (
                    <div key={h.id} style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #f5f5f5" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span className={STATUS_CLASS[h.status]}>{STATUS_LABELS[h.status]}</span>
                        <span style={{ fontSize: "12px", color: "#888" }}>{formatDateTime(h.created_at)}</span>
                      </div>
                      {h.changed_by_name && (
                        <p style={{ fontSize: "13px", color: "#555", margin: "4px 0 0" }}>by {h.changed_by_name}</p>
                      )}
                      {h.notes && <p style={{ margin: "4px 0 0" }}>{h.notes}</p>}
                      {h.rating && (
                        <p style={{ margin: "4px 0 0", color: "#f59e0b" }}>{"★".repeat(h.rating)}{"☆".repeat(5 - h.rating)}</p>
                      )}
                      {h.attachments?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                          {h.attachments.map((a) => (
                            a.kind === "photo" ? (
                              <img
                                key={a.id} src={a.file_url} alt="Attachment"
                                onClick={() => setViewerUrl(a.file_url)}
                                style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "6px", cursor: "pointer" }}
                              />
                            ) : (
                              <a key={a.id} href={a.file_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "13px" }}>📎 {a.file_url.split("/").pop()}</a>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>

          )
        }

      </div>

      <ImageLightbox url={viewerUrl} onClose={() => setViewerUrl(null)} />

    </div>

  );

}

export default ServiceTickets;
